<template>
    <default-calculator-layout :filter-width="filterWidth" :output-width="outputWidth" :calcFilter="calcFilter" :calculator-title="calculatorTitle">
        <div slot="filter">
            <number-input v-model="CurrentAppraisedHomeValue" :label="labelCurrentAppraisedHomeValue" :tooltip-link="CurrentAppraisedValueTooltip" inputmode="decimal" />
            <number-input v-model="AmountOwe" :label="labelAmountOwe" :tooltip-link="AmountOweTooltip" inputmode="decimal" />
            <drop-down v-model="CurrentCreditScore" :label="labelCurrentCreditScore" :tooltip-link="CurrentCreditScoreTooltip" :options="CreditScoreList" />
            <a class="calc-filter-link" :href="'/' + calcFilterLink">{{ CalcFilterLinkText }}</a>
        </div>
        <div slot="output">
            <div class="calculate-amount-section home-equity-section text-center">
                <h3>You can get a home equity loan or HELOC for:</h3>
                <span class="calculate-amount-home-equity">{{ CurrentCreditScore.toLowerCase() === 'poor' ? "$0" : homeEquityLoanComputed }}</span>
            </div>
            <div class="text-center">
                <button class="destination-btn" type="submit" @click="redirect()">{{ DestinationButtonText }}</button>
            </div>
            <div class="disclosures">
                <advertising-disclosures v-if="DisclosureText" :disclosure-pre-text="disclosurePreText" :disclosure-text="DisclosureText" :disclosure-tokens="DisclosureTokens" />
            </div>
            <lt-logo v-if="ltLogo==1">
                <small>NMLS#1136 Terms & Conditions Apply</small>
            </lt-logo>
        </div>
    </default-calculator-layout>
</template>

<script>
    import Accounting from 'accounting';
    import PaymentHelper from '~/helpers/payment';
    import CreditScoreList from '~/constants/credit-score-list';
    import BaseMixin from '~/mixin/base-mixin';
    import DefaultTooltip from '~/constants/tooltip-defaults';
    import LtLogo from '~/components/lt-logo.vue';
    import GraphQLMixin from '~/mixin/graphql';
    import LimitsFromZipByCode from '~/services/graphql/LimitsFromZipByCode.gql';
    import { getFinancialApiUrl, setHeader } from '~/helpers/financialapi-url-env';
    import DefaultCalculatorLayout from '~/layout/default-calculator.vue';

    export default {
        props: {
            labelCurrentAppraisedHomeValue: {
                default: 'Current Appraised Value of Home:',
            },
            labelAmountOwe: {
                default: 'How Much Do You Owe on Home:',
            },
            currentAppraisedHomeValue: {
                default: 225000,
            },
            amountOwe: {
                default: 165000,
            },
            homeEquityLoan: {
                default: 37500,
            },
            disclosureTokens: {
                default: '95',
            },
            disclosureText: {
                default: '',
            },
            disclosurePreText: {
                default: 'Monthly payments are an estimate and are for informational purposes. They do not represent a finance offer. Other tax, license, title, or fees may apply.',
            },
            currentAppraisedValueTooltip: {
                default: DefaultTooltip.CurrentAppraisedValue,
            },
            amountOweTooltip: {
                default: DefaultTooltip.AmountOwe,
            },
            destinationLinkOrId: {
                default: 'avm-homeequity',
            },
            destinationButtonText: {
                default: 'Access your Equity',
            },
            calcFilterLinkText: {
                default: 'Estimate your home\'s current value',
            },
            calcFilterLink: {
                default: 'home/home-value',
            },
            ltLogo: {
                default: 0,
            },
            graphQlUri: {
                default: '',
            },
            labelCurrentCreditScore: {
                default: 'Current Credit Score:',
            },
            currentCreditScore: {
                default: 'Excellent',
            },
            currentCreditScoreTooltip: {
                default: DefaultTooltip.CurrentCreditScore,
            },
        },
        data() {
            return {
                CurrentAppraisedHomeValue: this.currentAppraisedHomeValue,
                AmountOwe: this.amountOwe,
                CurrentAppraisedValueTooltip: parseInt(this.hideTooltip, 10) ? '' : this.currentAppraisedValueTooltip,
                AmountOweTooltip: parseInt(this.hideTooltip, 10) ? '' : this.amountOweTooltip,
                HomeEquityLoan: this.homeEquityLoan,
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
                CalcFilterLinkText: this.calcFilterLinkText,
                CalcFilterLink: this.calcFilterLink,
                GraphQlUri: this.graphQlUri,
                MaximumHomeEquityAmount: 0,
                CurrentCreditScore: this.currentCreditScore,
                CreditScoreList: CreditScoreList.homeEquityLoan,
                CurrentCreditScoreTooltip: this.currentCreditScoreTooltip,
            };
        },
        methods: {

            async getMaxLimit() {
                const variables = {
                    zip: '28277',
                    soaCode: 'GSE',
                };

                try {
                    const data = await this.graphQlQuery(LimitsFromZipByCode, variables);
                    const { limits } = data;
                    if (limits !== undefined) {
                        if (limits.limit1LivingUnit) {
                            this.MaximumHomeEquityAmount = limits.limit1LivingUnit;
                            if (Accounting.unformat(this.homeEquityLoanComputed) > this.MaximumHomeEquityAmount) {
                                this.homeEquityLoanComputed = this.MaximumHomeEquityAmount;
                            }
                        }
                    }
                } catch (e) {
                    console.warn(`Error while getting Maximum Home Equity Amount: ${e}`);
                }
            },
        },
        mounted() {
            this.GraphQlUri = this.graphQlUri !== '' ? this.graphQlUri : getFinancialApiUrl();
            this.createGraphQlClient(this.GraphQlUri, setHeader);
            this.getMaxLimit();
        },
        computed: {
            homeEquityLoanComputed() {
                let homeEquityLoan = this.getHomeEquityLoan;
                if (homeEquityLoan > this.MaximumHomeEquityAmount && this.MaximumHomeEquityAmount > 0) {
                    homeEquityLoan = this.MaximumHomeEquityAmount;
                }
                if (homeEquityLoan < 0) {
                    homeEquityLoan = 0;
                }
                return Accounting.formatMoney(Math.abs(homeEquityLoan), { precision: 0 });
            },
            getHomeEquityLoan() {
                return PaymentHelper.getHomeEquityLoan(Accounting.unformat(this.CurrentAppraisedHomeValue), Accounting.unformat(this.AmountOwe));
            },
        },
        components: {
            LtLogo,
            DefaultCalculatorLayout,
        },
        mixins: [BaseMixin, GraphQLMixin],
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="../../assets/styles/calc-filter-output.scss" lang="scss"></style>
<style lang="scss">
// Let's go doing heavy overrides ¯\_(ツ)_/¯
// Cannot do it any other way without breaking other calculators
@import "../../assets/styles/grove/grove-colors";
@import "../../assets/styles/grove/grove-sizes";
@import "../../assets/styles/grove/links";
@import "../../assets/styles/grove/buttons";
@import "../../assets/styles/grove/typographic";

.calc-filter {
    background-color: $lt-blue-light-4 !important;
    border-radius: $radius-2 !important;
    margin: $spacer-1 !important;
    padding: $spacer-4 $spacer-3 !important;
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.12) !important;
}

.form-group {
    label {
        color: $lt-gray-dark-4 !important;
        font-size: 16px !important;
        line-height: 1.5 !important;
    }

    select {
        border: 1px solid $lt-gray-dark-3 !important;
        border-radius: $radius-1 !important;

        background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAxOCAxMCI+CiAgPHBhdGggc3Ryb2tlPSIjMDA2OUJBIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS13aWR0aD0iMi4yNSIgZD0ibTIuMDA0IDEuNTIgNi45OTQgNy4yNDRhLjAxLjAxIDAgMCAwIC4wMTQgMEwxNiAxLjUyIi8+Cjwvc3ZnPgo=) !important;
        background-position-x: right 15px !important;
        background-position-y: center !important;
        background-repeat: no-repeat !important;
        background-color: $white !important;
        background-size: 20px !important;
    }

    .input-group {
        border: 1px solid $lt-gray-dark-3 !important;
        border-radius: $radius-1 !important;

        .input-group-addon {
            border: none !important;
            box-shadow: none !important;
            font-size: 24px !important;
            line-height: 1 !important;
        }

        input {
            border: none !important;
            box-shadow: none !important;
            padding-left: 0 !important;

            &:focus {
                background-color: $white !important;
            }
        }
    }

    .calc-question {
        color: $lt-blue !important;
    }
}

.calc-filter-link {
    @include link-common-important;
}

.destination-btn {
    @include button-common;
    @include button-size-large;
    @include button-primary-blue;
    min-width: 232px;
}

.calculate-amount-home-equity {
    color: $lt-blue-deep;
    font: $lt-font-secondary;
    font-size: 64px !important;
    line-height: 1.125 !important;
}

.calculate-amount-section {
    padding-bottom: $spacer-2b;

    h3 {
        color: $lt-blue-deep;
        font: $lt-font-primary;
        font-size: 21px !important;
        font-weight: bold !important;
        line-height: 1.38 !important;
    }
}
</style>
